import { HttpClient, HttpParams } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { MatDialog } from "@angular/material/dialog";
import { cloneDeep } from "lodash";
import { BehaviorSubject, catchError, map, of, switchMap, tap } from "rxjs";
import { environment } from "src/environments/environment";
import { IPayload } from "../interface/filters";
import { genActionPlanTableConfig } from "../modules/pages/action-plan/action-plan-table-config";
import { ActionPlan } from "../modules/pages/action-plan/models/action-plan";
import { ActionPlanAdapter } from "../modules/pages/action-plan/models/action-plan-adapter";

import { SummaryEntity } from "../modules/pages/hot-alert/configurations/hot-alert/summary-entity";
import { IApiService } from "../modules/records/models/api/i-api-service";
import { ActionPlanDialogComponent } from "../shared/dialogs/action-plan-dialog/action-plan-dialog.component";
import { ApplicationService } from "./application.service";
import { MyUtils } from "./my-utils.service";

export interface Outlet {
  addrCity: string;
  dealerId: string;
  entity: string;
  flagAfterInScope: string;
  flagSalesInScope: string;
  marketId: string;
  outletId: string;
  outletName: string;
  subRegion: string;
  fullName: string;
}

export interface QuestionByTab {
  chartType: "solidGaugeChart";
  questionCode: "stellantis-service_nPS_Dealer";
  questionTranslate: "Q2-Recommendation - workshop";
  questionType: "TenStarNPS";
  section: "AfterSales";
}

@Injectable({
  providedIn: "root",
})
export class ActionService implements IApiService<ActionPlan> {
  public actionPlanOpenedSubject: BehaviorSubject<any>;
  public actionPlanGroupingByStatusSubject: BehaviorSubject<any>;

  endpoint = environment.csibefrontend;
  isLoading = false;

  private _plans = new BehaviorSubject<ActionPlan[]>([]);
  get item$() {
    return this._plans.asObservable();
  }

  constructor(
    private http: HttpClient,
    private actionPlanAdapter: ActionPlanAdapter,
    private utils: MyUtils,
    private applicationService: ApplicationService,
    private dialog: MatDialog
  ) {
    this.actionPlanOpenedSubject = new BehaviorSubject<any>(
      JSON.parse(localStorage.getItem("actionPlanOpened")!)
    );
    this.actionPlanGroupingByStatusSubject = new BehaviorSubject<any>(
      JSON.parse(localStorage.getItem("actionPlanGroupingByStatus")!)
    );
  }

  getAll(filter: IPayload) {
    let payload: IPayload = Object.assign({}, filter || {});
    if (payload) {
      payload.question = {
        limit: "",
        locale: this.applicationService.languageSelectValue,
        orderByScore: "",
        questionCode: "",
        section: this.applicationService.navBarSelectValue.idItem,
        tab: this.applicationService.navBarSelectValue.idItem,
      };
    }
    this.isLoading = true;
    return this.http.post(this.endpoint + "v4/getActionPlanList", payload).pipe(
      map((res) => this.actionPlanAdapter.mapCollection(res)),
      catchError((err) => of(this.actionPlanAdapter.mapCollection())),
      tap((res) => this._plans.next(res.items)),
      tap((_) => (this.isLoading = false)),
      catchError((err) => {
        this.isLoading = false;
        return of(this.actionPlanAdapter.mapCollection());
      })
    );
  }

  public setActionPlanOpened(actionPlanOpenedSettings: any) {
    localStorage.setItem("actionPlanOpened", JSON.stringify(actionPlanOpenedSettings));
    this.actionPlanOpenedSubject.next(<any>actionPlanOpenedSettings);
  }

  public setactionPlanGroupingByStatus(actionPlanGroupingByStatusSettings: any) {
    localStorage.setItem(
      "actionPlanGroupingByStatus",
      JSON.stringify(actionPlanGroupingByStatusSettings)
    );
    this.actionPlanGroupingByStatusSubject.next(<any>actionPlanGroupingByStatusSettings);
  }

  getActionPlanOpened(body: any) {
    this.setActionPlanOpened({ loader: true, data: null, state: "waiting" });

    return this.http
      .post<{} | any[]>(this.endpoint + `getActionPlanOpened`, body)
      .subscribe((data: any) => {
        this.setActionPlanOpened({
          loader: false, 
          data: data,
          state: data ? "completed" : "error",
        });
      });
  }

  getActionPlanGroupingByStatus(body: any) {
    this.setactionPlanGroupingByStatus({
      loader: true,
      data: null,
      state: "waiting",
    });

    return this.http
      .post<{} | any[]>(this.endpoint + `getActionPlanGroupingByStatus`, body)
      .subscribe((data: any) => {
        this.setactionPlanGroupingByStatus({
          loader: false,
          data: data,
          state: data ? "completed" : "error",
        });
      });
  }

  delete(el: ActionPlan) {
    let body = this.actionPlanAdapter.generateDeleteBody(el);
    return this.http.post<{} | any[]>(this.endpoint + `v1/deleteActionPlan`, body);
  }

  closePlan(el: ActionPlan, formValue: any) {
    let body = this.actionPlanAdapter.generateCloseBody(el, formValue);
    return this.http.post<{} | any[]>(this.endpoint + `v1/closeActionPlan`, body);
  }

  print(el: ActionPlan) {
    alert("print");
    return of(null);
  }

  downloadExcel(el: ActionPlan) {
    let payload = cloneDeep(this.utils.settingsPayloadValue.payload);
    payload.question.section = this.applicationService.navBarSelectValue.idItem;
    payload.question.locale = this.applicationService.languageSelectValue;

    return this.http
      .post(this.endpoint + `v2/exportActionPlan`, payload, {
        responseType: "blob",
      })
      .pipe(
        tap((data) => {
          const file = new Blob([data], { type: "text/csv;charset=utf-8" });
          const fileURL = URL.createObjectURL(file);
          const a = document.createElement("a");
          a.href = fileURL;
          a.download = "exportActionPlan" + ".xlsx";
          a.target = "_blank";
          a.click();
        })
      );
  }

  create() {
    return this.dialog
      .open(ActionPlanDialogComponent, {
        maxHeight: "100vh",
        maxWidth: "100vh",
        panelClass: "actionPlanDialog"
      })
      .afterClosed()
      .pipe(switchMap((formData) => (formData ? this.createActionPlan(formData) : of(null))));
  }

  private createActionPlan(formValue: any) {
    let body = this.actionPlanAdapter.generateCreateBody(formValue);
    return this.http.post(this.endpoint + `v4/addActionPlan`, body);
  }

  getDealerOutletList() {
    let filter = this.utils.settingsPayloadValue.payload;
    return this.http.post<Outlet[]>(this.endpoint + `v1/getDealerOutletList`, filter);
  }

  getQuestionByTab() {
    let payload = {
      brand: this.utils.settingsPayloadValue.payload.network.brand.join(","),
      region: this.utils.settingsPayloadValue.payload.network.regions[0].region,
      tabName: "ACTION-PLAN",
      section: this.applicationService.navBarSelectValue.idItem,
      locale: this.applicationService.languageSelectValue,
      endvalidationdate: this.utils.settingsPayloadValue.payload.period.current.to
    };
    let params = new HttpParams();
    for (let [key, value] of Object.entries(payload)) {
      params = params.set(key, value);
    }
    return this.http.get<QuestionByTab[]>(this.endpoint + `v2/getQuestionsByTab`, {
      params,
    });
  }

  getSummaryInfo(filter: IPayload) {
    return this.http.post<SummaryEntity[]>(this.endpoint + "v4/getActionPlanSummary", filter).pipe(
      catchError((err) => {
        return of([]);
      })
    );
  }

  genTableConfig() {
    return genActionPlanTableConfig(this);
  }

  get hasMarket() {
    return (
      this.utils.settingsPayloadValue.payload.network.regions[0]?.subRegions[0]?.market.filter(
        (m) => !!m
      ).length > 0
    );
  }
}
